/**
 * InitialsAvatar molecule — a small circular initials avatar.
 *
 * Composed from atoms only, styled with semantic tokens (theme-safe):
 * `surface-subtle` circle + `content-primary` text.
 *
 * SE-7550 inverted this. It was `surface-inverse` + `content-inverse` — a black
 * disc with white letters — reasoned as "contrasts the card". The Figma binds
 * the opposite: asked for the variables behind a row that falls back to
 * initials (`70:1342`), the file answers `surface/subtle` (#f2f1f1) and
 * `content/primary` (#262627). A quiet grey disc, not a black one — the avatar
 * is a placeholder for a missing photo, and a black disc reads as emphasis the
 * player has not earned.
 *
 * Taken from the bound variables rather than sampled off a screenshot, per
 * `design-to-code-tickets.md` §5b: the raw scales invert across themes, so the
 * variable position is the theme-safe truth.
 * @module components/molecules/InitialsAvatar/InitialsAvatarView
 */
import React, { memo } from "react";

import { Box } from "@/components/atoms/Box";
import { Text } from "@/components/atoms/Text";

/** Props for InitialsAvatarView */
interface InitialsAvatarViewProps {
  /** 1–2 letter initials (derived by the caller) */
  readonly initials: string;
  /** Optional testID forwarded to the circle */
  readonly testID?: string;
  /**
   * 24px (`sm`, the default) or 44px (`lg`).
   *
   * Two sizes rather than a free number: 44px is what the capture frame's
   * player card draws, and a caller passing its own class would put the
   * circle's geometry back outside this file.
   */
  readonly size?: "sm" | "lg";
}

/** The circle's geometry, per size. */
const CIRCLE = {
  sm: "size-6",
  lg: "size-11",
} as const;

/** The initials' text variant, per size. */
const LETTERS = {
  sm: "micro-strong",
  lg: "body-strong",
} as const;

/**
 * Renders a circular initials avatar.
 * @param root0 - Component props
 * @param root0.initials - 1–2 letter initials
 * @param root0.testID - Optional testID
 * @param root0.size - 24px (`sm`) or 44px (`lg`)
 * @returns The avatar element.
 */
const InitialsAvatarView = ({
  initials,
  testID,
  size = "sm",
}: InitialsAvatarViewProps) => (
  <Box
    testID={testID}
    className={`${CIRCLE[size]} shrink-0 items-center justify-center rounded-full bg-surface-subtle`}
  >
    <Text variant={LETTERS[size]} className="text-content-primary">
      {initials}
    </Text>
  </Box>
);

InitialsAvatarView.displayName = "InitialsAvatarView";

export default memo(InitialsAvatarView);
